import { ChevronRight, LogOut } from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearAdminToken } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/custom/logo";
import { cn } from "@/lib/utils";
import { describeScope, NAV_GROUPS, pageFor, type PageScope, PRODUCT_NAME } from "./admin-nav";
import { type ProductKey, useAdminProduct } from "./hooks";

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const page = pageFor(location.pathname);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login");
  };

  return (
    <div className="technical-app-shell flex min-h-screen bg-slate-50">
      <aside className="sticky top-0 flex h-screen w-60 shrink-0 flex-col bg-gray-900 text-gray-300">
        <div className="px-4 py-5">
          <Logo theme="dark" size={36} />
        </div>
        <nav aria-label="Admin" className="flex-1 overflow-y-auto px-2 pb-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mt-4 first:mt-0">
              <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {group.label}
              </div>
              {group.items.map((item) => {
                const active = page?.item.to === item.to;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-2 rounded px-2 py-1.5 text-sm",
                      active ? "bg-gray-800 text-white" : "hover:bg-gray-800/60 hover:text-white"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="border-t border-gray-800 p-2">
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-800/60 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center justify-between gap-4 border-b border-slate-200 bg-white px-6">
          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            {page ? (
              <>
                <span className="text-slate-500">{page.group.label}</span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                <span className="truncate font-medium text-slate-900">{page.item.label}</span>
              </>
            ) : (
              <span className="font-medium text-slate-900">Admin console</span>
            )}
          </div>
          {page && <ProductScope scope={page.item.scope} />}
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The product a page works on. Pages that follow the switch get the switch; the rest say which
 * product they cover, so the switch is never shown where it does nothing.
 */
export function ProductScope({ scope }: { scope: PageScope }) {
  const { product, setProduct } = useAdminProduct();

  if (scope !== "switch") {
    return (
      <Badge variant="outline" className="shrink-0 font-sans text-xs font-normal normal-case tracking-normal">
        {describeScope(scope)}
      </Badge>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className="text-xs text-slate-500">Product</span>
      <div role="radiogroup" aria-label="Product" className="flex rounded-md border border-slate-200 p-0.5">
        {(Object.keys(PRODUCT_NAME) as ProductKey[]).map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={product === key}
            onClick={() => setProduct(key)}
            className={cn(
              "rounded px-3 py-1 text-sm",
              product === key ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            )}
          >
            {PRODUCT_NAME[key]}
          </button>
        ))}
      </div>
    </div>
  );
}
